import type { CodeConfirmationPrompt } from 'app/types/plan';
import { atomWithReset, useResetAtom } from 'jotai/utils';
import type { User } from 'app/types/user';
import type { Profile } from 'app/types/profile';
import type { ListingType } from 'app/types/listing';
import type { Channel } from 'app/types/chat';
import type * as Notifications from 'expo-notifications';
import { Dimensions, Platform } from 'react-native';
import { getWebWindow } from 'app/utils/web-window';

export const presentFestivalReminderAtom = atomWithReset<boolean>(false);
export const presentPayWallAtom = atomWithReset<boolean>(false);
export const presentNotificationModalAtom = atomWithReset<boolean>(false);
export type PublishPromptType = ListingType | 'post';
export const presentNewListingPrompt = atomWithReset<boolean | PublishPromptType>(false);
export const presentSignUpSheet = atomWithReset<boolean>(false);
export const presentGuestSignupPromptAtom = atomWithReset<boolean>(false);
export const presentLoginSheet = atomWithReset<boolean>(false);
export type AppDownloadPromptContext =
  { context: 'date-proposal'; firstName?: string } | { context: 'guest' } | { context: 'listing-published' };
export const presentAppDownloadPromptAtom = atomWithReset<false | AppDownloadPromptContext>(false);
export type AuthenticationGateSource =
  | 'banner'
  | 'contact'
  | 'favourite'
  | 'header'
  | 'member-profile'
  | 'propose-dates'
  | 'publish'
  | 'report'
  | 'reviews'
  | 'unknown';
export const authenticationGateSourceAtom = atomWithReset<AuthenticationGateSource>('unknown');
export const presentB2BActivatedModalAtom = atomWithReset<boolean>(false);
export const presentB2CPaidModalAtom = atomWithReset<boolean>(false);
export const selectedCodeConfirmationAtom = atomWithReset<{ userId: string; prompt: CodeConfirmationPrompt } | null>(
  null,
);
export const presentSubscriptionCodeModalAtom = atomWithReset<boolean>(false);
export const presentUserMetaModalAtom = atomWithReset<boolean>(false);
export const showIntroAfterSubscriptionCodeModalAtom = atomWithReset<boolean>(false);

type InsuredDetails = Pick<User, 'insured'> & { profile?: Pick<Profile, 'firstName'> };
export const presentInsuredDetailsAtom = atomWithReset<false | InsuredDetails>(false);
export const pauseModalsAtom = atomWithReset<boolean>(false);
export const codeFullWarningAtom = atomWithReset<boolean | string>(false);
export const presentCreateOfferModalAtom = atomWithReset<boolean>(false);
export const selectedCategoryAtom = atomWithReset<string[] | undefined>(undefined);
export const selectedServiceCategoryAtom = atomWithReset<string[] | undefined>(undefined);
export const selectedTagAtom = atomWithReset<string[] | undefined>(undefined);
export const completeProfilePromptCompletedAtom = atomWithReset<boolean>(false);

export const optimisticChannelsReadStatusAtom = atomWithReset<Record<Channel['channel_url'], boolean>>({}); // Used to optimistically show channel as read after the user has visited a channel.
export const optimisticUserProfilePictureAtom = atomWithReset<string | undefined>(undefined);

export const openedChannelAtom = atomWithReset<Channel | null>(null);
export const requestedMessageFooterChannelAtom = atomWithReset<Channel | null>(null);

export const optimisticListingHeroPictureAtom = atomWithReset<string | undefined>(undefined);
export const optimisticListingHeroPictureBeingUpdatedAtom = atomWithReset<string | undefined>(undefined);

export const optimisticFavouritesAtom = atomWithReset<Record<string, boolean>>({}); // listingId or groupingId -> isFavourite
export const alreadyRedirectedOnAppStartAtom = atomWithReset<boolean>(false);
export const babillardReadyAtom = atomWithReset<boolean>(false); // Indicates that the babillard screen has mounted and is ready to navigate to onboarding (or else) if needed.
export const lastNotificationResponseAtom = atomWithReset<Notifications.MaybeNotificationResponse>(null);
export const footerMarginBottomAtom = atomWithReset<number>(0);
export const bottomNavigationBarVisibleAtom = atomWithReset<boolean>(true);

// When true on web, we render the UI as if running on a mobile device (small viewport),
// even though the underlying platform is still "web".
// on a narrow browser the app could mount the desktop layout first and only later flip to mobile in an effect.
// Resolve native dimensions synchronously as well. Otherwise a phone initially renders
// the desktop layout until DeviceGate's effect runs, which can leave mobile headers
// without controls such as the chat back chevron.
const shouldForceMobileViewOnLoad =
  Platform.OS === 'web' ? (getWebWindow()?.innerWidth ?? Infinity) < 768 : Dimensions.get('window').width < 768;
export const forceMobileViewAtom = atomWithReset<boolean>(shouldForceMobileViewOnLoad);

export const newPostBannerShownAtom = atomWithReset<boolean>(false);
export function useResetUIAtoms() {
  const resetPresentFestivalReminder = useResetAtom(presentFestivalReminderAtom);
  const resetPresentPaywall = useResetAtom(presentPayWallAtom);
  const resetPresentNotificationModal = useResetAtom(presentNotificationModalAtom);
  const resetPresentNewListingPrompt = useResetAtom(presentNewListingPrompt);
  const resetPresentSignUpSheet = useResetAtom(presentSignUpSheet);
  const resetGuestSignupPrompt = useResetAtom(presentGuestSignupPromptAtom);
  const resetPresentLoginSheet = useResetAtom(presentLoginSheet);
  const resetPresentAppDownloadPrompt = useResetAtom(presentAppDownloadPromptAtom);
  const resetAuthenticationGateSource = useResetAtom(authenticationGateSourceAtom);
  const resetPresentB2BActivatedModalAtom = useResetAtom(presentB2BActivatedModalAtom);
  const resetPresentB2CPaidModalAtom = useResetAtom(presentB2CPaidModalAtom);
  const resetPauseModalsAtom = useResetAtom(pauseModalsAtom);
  const resetCodeFullWarningAtom = useResetAtom(codeFullWarningAtom);
  const resetSelectedCodeConfirmation = useResetAtom(selectedCodeConfirmationAtom);
  const resetPresentSubscriptionCodeModalAtom = useResetAtom(presentSubscriptionCodeModalAtom);
  const resetPresentUserMetaModalAtom = useResetAtom(presentUserMetaModalAtom);
  const resetShowIntroAfterSubscriptionCodeModalAtom = useResetAtom(showIntroAfterSubscriptionCodeModalAtom);
  const resetPresentInsuredDetailsAtom = useResetAtom(presentInsuredDetailsAtom);
  const resetPresentCreateOfferModalAtom = useResetAtom(presentCreateOfferModalAtom);
  const resetSelectedCategoryAtom = useResetAtom(selectedCategoryAtom);
  const resetSelectedServiceCategoryAtom = useResetAtom(selectedServiceCategoryAtom);
  const resetSelectedTagAtom = useResetAtom(selectedTagAtom);
  const resetOptimisticChannelsReadStatusAtom = useResetAtom(optimisticChannelsReadStatusAtom);
  const resetOptimisticUserProfilePictureAtom = useResetAtom(optimisticUserProfilePictureAtom);
  const resetOptimisticListingHeroPictureAtom = useResetAtom(optimisticListingHeroPictureAtom);
  const resetOptimisticListingHeroPictureBeingUpdatedAtom = useResetAtom(optimisticListingHeroPictureBeingUpdatedAtom);
  const resetOptimisticFavouritesAtom = useResetAtom(optimisticFavouritesAtom);
  const resetAlreadyRedirectedOnAppStartAtom = useResetAtom(alreadyRedirectedOnAppStartAtom);
  const resetLastNotificationResponseAtom = useResetAtom(lastNotificationResponseAtom);
  const resetBabillardReadyAtom = useResetAtom(babillardReadyAtom);
  const resetOpenedChannelAtom = useResetAtom(openedChannelAtom);
  const resetRequestedMessageFooterChannelAtom = useResetAtom(requestedMessageFooterChannelAtom);
  const resetFooterMarginBottomAtom = useResetAtom(footerMarginBottomAtom);
  const resetBottomNavigationBarVisibleAtom = useResetAtom(bottomNavigationBarVisibleAtom);
  const resetNewPostBannerShownAtom = useResetAtom(newPostBannerShownAtom);
  const resetCompleteProfilePromptCompletedAtom = useResetAtom(completeProfilePromptCompletedAtom);
  return () => {
    resetPresentFestivalReminder();
    resetPresentPaywall();
    resetPresentNotificationModal();
    resetPresentNewListingPrompt();
    resetPresentSignUpSheet();
    resetGuestSignupPrompt();
    resetPresentLoginSheet();
    resetPresentAppDownloadPrompt();
    resetAuthenticationGateSource();
    resetPresentB2BActivatedModalAtom();
    resetPresentB2CPaidModalAtom();
    resetSelectedCodeConfirmation();
    resetPresentSubscriptionCodeModalAtom();
    resetPresentUserMetaModalAtom();
    resetPauseModalsAtom();
    resetCodeFullWarningAtom();
    resetPresentInsuredDetailsAtom();
    resetPresentCreateOfferModalAtom();
    resetSelectedCategoryAtom();
    resetSelectedServiceCategoryAtom();
    resetSelectedTagAtom();
    resetShowIntroAfterSubscriptionCodeModalAtom();
    resetOptimisticChannelsReadStatusAtom();
    resetOptimisticUserProfilePictureAtom();
    resetOptimisticListingHeroPictureAtom();
    resetOptimisticListingHeroPictureBeingUpdatedAtom();
    resetOptimisticFavouritesAtom();
    resetAlreadyRedirectedOnAppStartAtom();
    resetLastNotificationResponseAtom();
    resetBabillardReadyAtom();
    resetOpenedChannelAtom();
    resetRequestedMessageFooterChannelAtom();
    resetFooterMarginBottomAtom();
    resetBottomNavigationBarVisibleAtom();
    resetNewPostBannerShownAtom();
    resetCompleteProfilePromptCompletedAtom();
  };
}
